import React, { useState } from 'react';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useMsal } from '@azure/msal-react';

const isURL = (input) => /^(ftp|http|https):\/\/[^ "]+$/.test(input);
const isGitHubURL = (input) => input.toLowerCase().includes('github');

const verticalOptions = [
    "Healthcare", "Aerospace", "Media", "Automotive", "Industrial",
    "Consumer Devices", "Semiconductor", "Plant Engineering",
    "Telecom", "Rail", "Oil & Gas", "Other"
];

const ReadyToPublishModal = ({ onClose, onSubmit, selectedRowData }) => {
    const { instance } = useMsal()
    const [formData, setFormData] = useState({
        GitHubLink: '',
        HostedURL: '',
        DesignDocs: '',
        HowToUseHURL: '',
        OpenSrcLib: '',
        ChooseVertical: '',
        TypeOfComponent: '',
        JsonFile: null,
        UploadIcon: null,
        UploadBGIcon: null,
        checkbox: false,
    });

    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        const { name, type, value, checked, files } = e.target;
        if (type === 'checkbox') {
            setFormData({ ...formData, [name]: checked });
        } else if (files) {
            setFormData({ ...formData, [name]: files[0] });
        } else {
            setFormData({ ...formData, [name]: value });
        }
        setErrors({ ...errors, [name]: '' });
    };

    const validate = () => {
        const newErrors = {};
        if (!isGitHubURL(formData.GitHubLink)) newErrors.GitHubLink = 'Enter a valid GitHub URL';
        if (!isURL(formData.HostedURL)) newErrors.HostedURL = 'Enter a valid Hosted URL';
        if (!isURL(formData.DesignDocs)) newErrors.DesignDocs = 'Enter a valid Design Document URL';
        if (!isURL(formData.HowToUseHURL)) newErrors.HowToUseHURL = 'Enter a valid Usage URL';
        if (!isURL(formData.OpenSrcLib)) newErrors.OpenSrcLib = 'Enter a valid SonarQube URL';
        if (!formData.ChooseVertical) newErrors.ChooseVertical = 'Choose a vertical';
        if (!formData.TypeOfComponent) newErrors.TypeOfComponent = 'Select type';
        if (!formData.JsonFile) newErrors.JsonFile = 'JSON file required';
        if (!formData.UploadIcon) newErrors.UploadIcon = 'Default icon required';
        if (!formData.UploadBGIcon) newErrors.UploadBGIcon = 'Active icon required';
        if (!formData.checkbox) newErrors.checkbox = 'Please accept terms';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const getValidationIcon = (field, urlType = false) => {
        const value = formData[field];
        if (!value) return null;
        const valid = urlType ? isURL(value) : isGitHubURL(value);
        return valid ? (
            <CheckCircleIcon className="w-5 h-5 text-green-500 ml-2" />
        ) : (
            <XCircleIcon className="w-5 h-5 text-red-500 ml-2" />
        );
    };

    const handleSubmit = () => {
        if (validate()) {
            const data = new FormData();

            // Form input fields
            data.append('GitHubLink', formData.GitHubLink);
            data.append('HostedURL', formData.HostedURL);
            data.append('DesignDocs', formData.DesignDocs);
            data.append('HowToUseHURL', formData.HowToUseHURL);
            data.append('OpenSrcLib', formData.OpenSrcLib);
            data.append('ChooseVertical', formData.ChooseVertical);
            data.append('TypeOfComponent', formData.TypeOfComponent);
            data.append('checkbox', formData.checkbox);

            // Files
            if (formData.JsonFile) data.append('inputJson', formData.JsonFile);
            if (formData.UploadIcon) data.append('UploadIcon', formData.UploadIcon);
            if (formData.UploadBGIcon) data.append('UploadBGIcon', formData.UploadBGIcon);

            // MSAL info
            const account = instance.getAllAccounts()[0];
            if (account) {
                data.append('Email', account.username.toLowerCase() || '');
                data.append('SenderName', account.name || '');
            }

            // Selected component data
            data.append('ComponentName', selectedRowData.ComponentName || '');
            data.append('ComponentDetails', selectedRowData.ComponentDetails || '');
            data.append('Category', selectedRowData.Category || '');
            data.append('Technology', selectedRowData.Technology || '');

            // Static value
            data.append('ComponentState', 'Ready to Review');

            onSubmit(data); // send to Table.jsx or API
        }
    };


    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-lg w-full max-w-4xl h-[500px] flex flex-col">
                {/* Header */}
                <div className="bg-blue-600 text-white text-lg font-semibold p-4 rounded-t-lg">
                    Ready To Publish - Fill the Details
                </div>

                {/* Body (Scrollable) */}
                <div className="overflow-y-auto flex-grow px-6 py-4 space-y-4">

                    {/* Input Fields */}
                    {[
                        { label: 'GitHub URL', name: 'GitHubLink', type: 'text', url: true },
                        { label: 'Hosted URL', name: 'HostedURL', type: 'text', url: true },
                        { label: 'Design Document', name: 'DesignDocs', type: 'text', url: true },
                        { label: 'How to use Hosted URL', name: 'HowToUseHURL', type: 'text', url: true },
                        { label: 'SonarQube Tested URL', name: 'OpenSrcLib', type: 'text', url: true }
                    ]?.map((field) => (
                        <div key={field.name} className="flex items-center gap-4">
                            <label className="w-64 text-sm font-medium text-gray-700">
                                {field.label} <span className="text-red-500">*</span>
                            </label>
                            <div className="flex-1 flex items-center">
                                <input
                                    type="text"
                                    name={field.name}
                                    value={formData[field.name]}
                                    onChange={handleChange}
                                    placeholder="Enter URL"
                                    className="w-full border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                                />
                                {field.url && getValidationIcon(field.name, field.name !== 'GitHubLink')}
                            </div>
                            {errors[field.name] && <p className="text-sm text-red-600 w-full">{errors[field.name]}</p>}
                        </div>
                    ))}

                    {/* Choose Vertical */}
                    <div className="flex items-center gap-4">
                        <label className="w-64 text-sm font-medium text-gray-700">Choose Vertical <span className="text-red-500">*</span></label>
                        <select
                            name="ChooseVertical"
                            value={formData.ChooseVertical}
                            onChange={handleChange}
                            className="w-[67%] border border-gray-300 rounded px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                            <option value="">Select Vertical</option>
                            {verticalOptions?.map((option) => (
                                <option key={option} value={option} className='text-sm'>{option}</option>
                            ))}
                        </select>
                        {errors.ChooseVertical && <p className="text-sm text-red-600 w-full">{errors.ChooseVertical}</p>}
                    </div>

                    {/* Type of Component */}
                    <div className="flex items-center gap-4">
                        <label className="w-64 text-sm font-medium text-gray-700">Type of Component <span className="text-red-500">*</span></label>
                        <div className="flex gap-4">
                            <label className="text-sm"><input type="radio" name="TypeOfComponent" value="UI" onChange={handleChange} checked={formData.TypeOfComponent === 'UI'} className="mr-2" /> UI</label>
                            <label className="text-sm"><input type="radio" name="TypeOfComponent" value="Non-UI" onChange={handleChange} checked={formData.TypeOfComponent === 'Non-UI'} className="mr-2" /> Non-UI</label>
                        </div>
                        {errors.TypeOfComponent && <p className="text-sm text-red-600 w-full">{errors.TypeOfComponent}</p>}
                    </div>

                    {/* JSON File */}
                    <div className="flex items-center gap-4">
                        <label className="w-64 text-sm font-medium text-gray-700">Input JSON File <span className="text-red-500">*</span></label>
                        <input type="file" name="JsonFile" accept=".json" onChange={handleChange} />
                        {errors.JsonFile && <p className="text-sm text-red-600 w-full">{errors.JsonFile}</p>}
                    </div>

                    {/* Icons */}
                    <div className="flex items-center gap-4">
                        <label className="w-64 text-sm font-medium text-gray-700">Default Icon <span className="text-red-500">*</span></label>
                        <input type="file" name="UploadIcon" accept="image/*" onChange={handleChange} />
                        {errors.UploadIcon && <p className="text-sm text-red-600 w-full">{errors.UploadIcon}</p>}
                    </div>

                    <div className="flex items-center gap-4">
                        <label className="w-64 text-sm font-medium text-gray-700">Active Icon <span className="text-red-500">*</span></label>
                        <input type="file" name="UploadBGIcon" accept="image/*" onChange={handleChange} />
                        {errors.UploadBGIcon && <p className="text-sm text-red-600 w-full">{errors.UploadBGIcon}</p>}
                    </div>

                    {/* Checkbox */}
                    <div className="flex items-center gap-2">
                        <input type="checkbox" name="checkbox" checked={formData.checkbox} onChange={handleChange} />
                        <label className="text-sm text-gray-700">Code is using only open-source libraries <span className="text-red-500">*</span></label>
                        {errors.checkbox && <p className="text-sm text-red-600 ml-4">{errors.checkbox}</p>}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-4 border-t p-4">
                    <button onClick={onClose} className="px-4 py-2 border border-blue-600 text-blue-600 rounded hover:bg-blue-50">
                        CANCEL
                    </button>
                    <button onClick={handleSubmit} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                        SUBMIT
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ReadyToPublishModal;
